import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import logoBiat from '../assets/logo.png';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem
} from '@mui/material';
import {
  Menu as MenuIcon,
  Assignment,
  Dashboard,
  History,
  Logout,
  Person,
  Email,
  Folder
} from '@mui/icons-material';
import { logout, getUserInfo } from '../utils/auth';
import { getCurrentUser } from '../api/alfresco';

const drawerWidth = 260;

const menuItems = [
  { text: 'Dashboard BIAT', icon: <Dashboard />, path: '/dashboard' },
  { text: 'Mes tâches', icon: <Assignment />, path: '/tasks' },
  { text: 'Documents', icon: <Folder />, path: '/documents' },
  { text: 'Historique', icon: <History />, path: '/history' }
];

const Layout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [userInfo, setUserInfo] = useState(null);

  // Charger les informations utilisateur au montage et à chaque changement de location
  useEffect(() => {
    const loadUserInfo = async () => {
      // D'abord, essayer de récupérer depuis le localStorage
      let info = getUserInfo();
      console.log('Layout - UserInfo depuis localStorage:', info);
      
      // Si les informations ne sont pas disponibles ou incomplètes, essayer de les récupérer depuis l'API
      if (!info || !info.firstName) {
        try {
          console.log('Layout - Tentative de récupération depuis l\'API...');
          info = await getCurrentUser();
          console.log('Layout - UserInfo depuis API:', info);
          if (info) {
            console.log('Layout - UserInfo depuis API - Propriétés:', Object.keys(info));
          }
        } catch (error) {
          console.warn('Layout - Impossible de récupérer les informations utilisateur:', error);
        }
      }
      
      setUserInfo(info);
    };
    
    loadUserInfo();
  }, [location]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleNavigation = (path) => {
    navigate(path);
    setMobileOpen(false);
  };

  // Fonction pour obtenir les initiales de l'utilisateur
  const getInitials = () => {
    if (!userInfo) return 'U';
    
    // Essayer différentes propriétés possibles
    const firstName = userInfo.firstName || userInfo.firstname || '';
    const lastName = userInfo.lastName || userInfo.lastname || '';
    const displayName = userInfo.displayName || userInfo.displayname || '';
    
    if (firstName && lastName) {
      return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
    }
    if (firstName) {
      return firstName.charAt(0).toUpperCase();
    }
    if (displayName) {
      const parts = displayName.trim().split(/\s+/);
      if (parts.length >= 2) {
        return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase();
      }
      return displayName.charAt(0).toUpperCase();
    }
    if (userInfo.id) {
      return userInfo.id.charAt(0).toUpperCase();
    }
    if (userInfo.userName) {
      return userInfo.userName.charAt(0).toUpperCase();
    }
    return 'U';
  };

  // Fonction pour obtenir le nom d'affichage
  const getDisplayName = () => {
    if (!userInfo) {
      console.log('Layout - getDisplayName: userInfo est null');
      return 'Utilisateur';
    }
    
    // Log toutes les propriétés disponibles pour déboguer
    console.log('Layout - getDisplayName - userInfo complet:', JSON.stringify(userInfo, null, 2));
    console.log('Layout - getDisplayName - Propriétés disponibles:', Object.keys(userInfo));
    
    // Essayer différentes propriétés possibles
    const firstName = userInfo.firstName || userInfo.firstname || '';
    const lastName = userInfo.lastName || userInfo.lastname || '';
    const displayName = userInfo.displayName || userInfo.displayname || '';
    const fullName = userInfo.fullName || userInfo.fullname || '';
    
    console.log('Layout - getDisplayName - firstName:', firstName, 'lastName:', lastName, 'displayName:', displayName, 'fullName:', fullName);
    
    // Si on a prénom et nom, les combiner
    if (firstName && lastName) {
      const result = `${firstName} ${lastName}`;
      console.log('Layout - getDisplayName - Résultat (prénom + nom):', result);
      return result;
    }
    
    // Si on a juste le prénom
    if (firstName) {
      console.log('Layout - getDisplayName - Résultat (prénom):', firstName);
      return firstName;
    }
    
    // Si on a un displayName
    if (displayName) {
      console.log('Layout - getDisplayName - Résultat (displayName):', displayName);
      return displayName;
    }
    
    // Si on a un fullName
    if (fullName) {
      console.log('Layout - getDisplayName - Résultat (fullName):', fullName);
      return fullName;
    }
    
    // Sinon utiliser l'ID
    const result = userInfo.id || userInfo.userName || 'Utilisateur';
    console.log('Layout - getDisplayName - Résultat (ID/userName):', result);
    return result;
  };

  const drawer = (
    <Box sx={{ 
      height: '100%', 
      display: 'flex', 
      flexDirection: 'column',
      background: 'linear-gradient(180deg, #047857 0%, #065F46 100%)',
      position: 'relative',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'radial-gradient(circle at top right, rgba(16, 185, 129, 0.15) 0%, transparent 50%)',
        pointerEvents: 'none'
      }
    }}>
      {/* Logo Section - Premium */}
      <Box sx={{ 
        py: 3,
        px: 3,
        display: 'flex', 
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        zIndex: 1
      }}>
        <Box sx={{
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          borderRadius: 3,
          p: 2,
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.2)'
        }}>
          <img 
            src={logoBiat} 
            alt="BIAT Logo" 
            style={{ height: '42px', width: 'auto', display: 'block' }}
          />
        </Box>
      </Box>

      <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.1)', mx: 2 }} />

      {/* Navigation Menu - Premium Style */}
      <List sx={{ px: 2, pt: 3, pb: 2, flexGrow: 1, position: 'relative', zIndex: 1 }}>
        {menuItems.map((item) => (
          <ListItem key={item.text} disablePadding sx={{ mb: 1 }}>
            <ListItemButton
              selected={location.pathname === item.path}
              onClick={() => handleNavigation(item.path)}
              sx={{
                borderRadius: 2.5,
                color: '#ffffff',
                backgroundColor: 'transparent',
                position: 'relative',
                overflow: 'hidden',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  left: 0,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: 4,
                  height: 0,
                  backgroundColor: '#10B981',
                  borderRadius: '0 4px 4px 0',
                  transition: 'height 0.3s ease'
                },
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.12)',
                  transform: 'translateX(4px)',
                  color: '#ffffff',
                  '&::before': {
                    height: '70%'
                  },
                  '& .MuiListItemIcon-root': {
                    color: '#ffffff'
                  },
                  '& .MuiListItemText-primary': {
                    color: '#ffffff'
                  }
                },
                '&.Mui-selected': {
                  backgroundColor: 'rgba(255, 255, 255, 0.25)',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
                  color: '#ffffff',
                  '&::before': {
                    height: '100%'
                  },
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.3)',
                    color: '#ffffff'
                  },
                  '& .MuiListItemIcon-root': {
                    color: '#ffffff'
                  },
                  '& .MuiListItemText-primary': {
                    color: '#ffffff'
                  }
                },
                py: 1.75,
                px: 2.5
              }}
            >
              <ListItemIcon 
                sx={{ 
                  color: '#ffffff',
                  minWidth: 44,
                  transition: 'transform 0.3s ease',
                  '.Mui-selected &': {
                    transform: 'scale(1.1)'
                  }
                }}
              >
                {item.icon}
              </ListItemIcon>
              <ListItemText 
                primary={item.text}
                primaryTypographyProps={{
                  fontWeight: location.pathname === item.path ? 700 : 600,
                  fontSize: '0.9375rem',
                  letterSpacing: '0.01em',
                  color: '#ffffff'
                }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>

      {/* Recent Projects - Premium Card */}
      <Box sx={{ px: 2.5, pb: 3, position: 'relative', zIndex: 1 }}>
        <Typography 
          variant="caption" 
          sx={{ 
            color: 'rgba(255, 255, 255, 0.85)',
            display: 'block',
            mb: 1.5,
            fontSize: '0.6875rem',
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.1em'
          }}
        >
          Projets récents
        </Typography>
        <Box sx={{ 
          background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)',
          borderRadius: 2.5,
          p: 2,
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 6px 16px rgba(0, 0, 0, 0.15)'
          }
        }}>
          <Typography 
            variant="body2" 
            sx={{ 
              color: '#ffffff',
              fontSize: '0.875rem',
              fontWeight: 600,
              mb: 0.5
            }}
          >
            Workflows BIAT
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              backgroundColor: '#10B981',
              animation: 'pulse 2s ease-in-out infinite',
              '@keyframes pulse': {
                '0%, 100%': { opacity: 1 },
                '50%': { opacity: 0.5 }
              }
            }} />
            <Typography 
              variant="caption" 
              sx={{ 
                color: 'rgba(255, 255, 255, 0.95)',
                fontSize: '0.75rem',
                fontWeight: 500
              }}
            >
              Actif
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Footer - Elegant */}
      <Box sx={{ 
        px: 3,
        pb: 2.5,
        pt: 2,
        borderTop: '1px solid rgba(255, 255, 255, 0.1)',
        position: 'relative',
        zIndex: 1
      }}>
        <Typography 
          variant="caption" 
          sx={{ 
            color: 'rgba(255, 255, 255, 0.75)',
            display: 'block',
            textAlign: 'center',
            fontSize: '0.6875rem',
            fontWeight: 500,
            letterSpacing: '0.02em'
          }}
        >
          BIAT Assurance © 2025
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
          backgroundColor: '#ffffff',
          borderBottom: '1px solid',
          borderColor: 'divider',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04)'
        }}
      >
        <Toolbar sx={{ py: 1.5 }}>
          <IconButton
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ 
              mr: 2, 
              display: { sm: 'none' },
              color: 'primary.main',
              '&:hover': {
                backgroundColor: 'rgba(6, 95, 70, 0.08)'
              }
            }}
          >
            <MenuIcon />
          </IconButton>
          <Typography 
            variant="h6" 
            noWrap 
            component="div" 
            sx={{ 
              flexGrow: 1,
              color: 'primary.main',
              fontWeight: 700,
              fontSize: '1.25rem',
              letterSpacing: '-0.01em'
            }}
          >
            Gestion des Workflows
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
              <Typography 
                variant="body2" 
                sx={{ 
                  fontWeight: 600, 
                  lineHeight: 1.2,
                  color: 'text.primary',
                  fontSize: '0.9375rem'
                }}
              >
                {getDisplayName()}
              </Typography>
              {userInfo?.email && (
                <Typography 
                  variant="caption" 
                  sx={{ 
                    color: 'text.secondary', 
                    fontSize: '0.75rem',
                    fontWeight: 500
                  }}
                >
                  {userInfo.email}
                </Typography>
              )}
            </Box>
            <IconButton
              size="small"
              onClick={handleMenuClick}
              sx={{ 
                ml: 0.5,
                p: 0.5,
                transition: 'all 0.2s ease',
                '&:hover': {
                  transform: 'scale(1.05)',
                  backgroundColor: 'rgba(6, 95, 70, 0.08)'
                }
              }}
              aria-controls={anchorEl ? 'account-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={anchorEl ? 'true' : undefined}
            >
              <Avatar 
                sx={{ 
                  width: 40, 
                  height: 40,
                  bgcolor: 'primary.main',
                  color: '#ffffff',
                  fontSize: '0.9375rem',
                  fontWeight: 700,
                  boxShadow: '0 2px 8px rgba(6, 95, 70, 0.25)',
                  border: '2px solid',
                  borderColor: 'primary.light'
                }}
              >
                {getInitials()}
              </Avatar>
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              id="account-menu"
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
              PaperProps={{
                sx: {
                  mt: 2,
                  minWidth: 280,
                  borderRadius: 3,
                  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
                  border: '1px solid',
                  borderColor: 'divider',
                  overflow: 'hidden'
                }
              }}
            >
              <Box sx={{ 
                px: 3, 
                py: 2.5, 
                background: 'linear-gradient(135deg, rgba(6, 95, 70, 0.05) 0%, rgba(6, 95, 70, 0.02) 100%)',
                borderBottom: '1px solid',
                borderColor: 'divider'
              }}>
                <Typography 
                  variant="subtitle1" 
                  sx={{ 
                    fontWeight: 700, 
                    color: 'primary.main',
                    mb: 0.5,
                    fontSize: '1rem'
                  }}
                >
                  {getDisplayName()}
                </Typography>
                {userInfo?.email && (
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                    <Email sx={{ fontSize: 16, mr: 0.75, color: 'text.secondary' }} />
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: 'text.secondary',
                        fontSize: '0.8125rem'
                      }}
                    >
                      {userInfo.email}
                    </Typography>
                  </Box>
                )}
                {userInfo?.id && (
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      color: 'text.secondary',
                      display: 'block',
                      fontSize: '0.75rem',
                      fontWeight: 500
                    }}
                  >
                    ID: {userInfo.id}
                  </Typography>
                )}
              </Box>
              <MenuItem 
                onClick={handleLogout}
                sx={{ 
                  mx: 1,
                  my: 1,
                  borderRadius: 2,
                  py: 1.5,
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: 'rgba(239, 68, 68, 0.08)',
                    transform: 'translateX(4px)'
                  }
                }}
              >
                <ListItemIcon>
                  <Logout fontSize="small" sx={{ color: 'error.main' }} />
                </ListItemIcon>
                <ListItemText 
                  primary="Déconnexion" 
                  primaryTypographyProps={{ 
                    sx: { 
                      color: 'error.main', 
                      fontWeight: 600,
                      fontSize: '0.9375rem'
                    } 
                  }} 
                />
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
        aria-label="navigation"
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth }
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth }
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2, sm: 3, md: 4 },
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          backgroundColor: 'background.default',
          minHeight: '100vh'
        }}
      >
        <Toolbar />
        <Box sx={{ mt: 1 }}>
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
};

export default Layout;

